import { AxiosError } from 'axios';
import { RequestStrings } from './strings';
import { getProxyAgentErrorBody, getProxyAgentTunnelErrorMessage } from '../connectivity/utils/proxyAgentErrors';

/**
 * Takes `undefined` because callers hand it one.
 *
 * Every consumer reads an `error` off a hook whose type is `AxiosError | undefined`, and the state
 * can hold `undefined` while the UI state says errored — a request that rejects with nothing (a
 * cancellation) puts it there. The old signature said that could not happen, so call sites either
 * cast around it or, as the run chart did, threw a TypeError inside an error boundary while trying
 * to render the error message.
 *
 * An absent error is treated as a connection failure: something went wrong and there is no response
 * to describe it, which is exactly what the no-response branch below already says.
 */
export function getErrorMessageFromAxiosError(error?: AxiosError) {
  if (!error) {
    return RequestStrings.CONNECTION_ERROR_MSG;
  }

  if (error.isAxiosError && error.code === AxiosError.ERR_NETWORK) {
    return RequestStrings.CONNECTION_ERROR_SHORT;
  }

  if (!error.response) {
    return RequestStrings.CONNECTION_ERROR_MSG;
  }

  if (!error.response.status || error.response.status <= 0) {
    return RequestStrings.CONNECTION_ERROR_MSG;
  }

  if (error.response.status === 422) {
    return RequestStrings.INVALID_INPUT_MSG;
  }

  // A source whose proxy agent tunnel is unusable answers 503 with a typed body; say what to do
  // about it rather than echoing the tunnel manager's wording.
  const proxyAgentMessage = getProxyAgentTunnelErrorMessage(getProxyAgentErrorBody(error));

  if (proxyAgentMessage) {
    return proxyAgentMessage;
  }

  // A gateway failure (502 from APISIX, say) often carries no body at all. Defaulting it keeps the
  // reads below from throwing a TypeError inside the caller — including hermesCourierService's own
  // `catchError`, where it would replace the request's error with an unrelated one.
  const errorResponse = (error.response.data || {}) as {
    error_message?: string;
    message?: string;
  };

  return errorResponse.error_message || errorResponse.message || error.message;
}
